import React, { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Languages } from "lucide-react";

import { useLanguage } from "./LanguageContext";
import "./LanguageSelector.css";

export default function LanguageSelector({ className = "", onLanguageChanged }) {
  const { language, languages, setLanguage, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const chooseLanguage = async (nextLanguage) => {
    // A deferred reviewed catalogue may need to arrive before the preference
    // becomes active. Awaiting the context contract avoids a momentary English
    // repaint after an operator deliberately chooses another language.
    const applied = await Promise.resolve(setLanguage(nextLanguage.code));
    if (applied) onLanguageChanged?.(nextLanguage);
    setOpen(false);
    window.requestAnimationFrame(() => triggerRef.current?.focus());
  };

  return (
    <div ref={rootRef} className={`crm-language-selector ${className}`.trim()}>
      <button
        ref={triggerRef}
        type="button"
        className="crm-language-selector__trigger"
        aria-label={`${t("language.change")}: ${language.nativeName}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={t("language.change")}
        onClick={() => setOpen((value) => !value)}
      >
        <Languages size={18} aria-hidden="true" />
        <span className="crm-language-selector__code">{language.shortLabel}</span>
        <ChevronDown className="crm-language-selector__chevron" size={15} aria-hidden="true" />
      </button>

      {open && (
        <div className="crm-language-selector__menu" role="listbox" aria-label={t("language.choose")}>
          <div className="crm-language-selector__heading">
            <strong>{t("language.choose")}</strong>
            <span>{t("language.description")}</span>
          </div>
          <div className="crm-language-selector__options">
            {languages.map((option) => {
              const selected = option.code === language.code;
              return (
                <button
                  key={option.code}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className="crm-language-selector__option"
                  data-selected={selected ? "true" : "false"}
                  onClick={() => chooseLanguage(option)}
                >
                  <span className="crm-language-selector__option-copy">
                    <strong>{option.nativeName}</strong>
                    <small>{option.locale}</small>
                  </span>
                  {selected && <Check size={17} aria-label={option.nativeName} />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
